import { X, ExternalLink, Bookmark, BookmarkCheck, Building2, IndianRupee, CalendarClock, Hash, Globe, AlertTriangle } from "lucide-react";
import { useEffect } from "react";

const Lbl = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">{children}</div>
);

function parseDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d) ? null : d;
}

function isClosed(dateStr) {
  const d = parseDate(dateStr);
  return !!d && d.getTime() <= Date.now();
}

function daysUntil(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return null;
  const diff = d.getTime() - Date.now();
  return diff <= 0 ? 0 : Math.ceil(diff / 86400000);
}

function closesToday(dateStr) {
  const d = parseDate(dateStr);
  if (!d || isClosed(dateStr)) return false;
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(d) === fmt.format(new Date());
}

function formatDateIST(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return dateStr || "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  }).format(d) + " IST";
}

function relevanceClass(score) {
  if (typeof score !== "number") return "bg-[#F1F5F9] text-[#64748B]";
  if (score <= 45) return "bg-[#FEE2E2] text-[#DC2626]";
  if (score <= 70) return "bg-[#FEF3C7] text-[#92400E]";
  return "bg-[#DCFCE7] text-[#15803D]";
}

export default function TenderModal({ tender, projectName, open, onClose, isBookmarked, onToggleBookmark }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !tender) return null;

  const t = tender;
  const dleft = daysUntil(t.closing_date);
  const closed = isClosed(t.closing_date);

  let deadline;
  if (closed) deadline = "Closed";
  else if (dleft === null) deadline = "No deadline";
  else if (closesToday(t.closing_date)) deadline = "Closes today";
  else deadline = `${dleft} days left`;

  const fmtOpeningDate = formatDateIST(t.opening_date);
  const fmtClosingDate = formatDateIST(t.closing_date);

  const Field = ({ icon: Icon, label, value }) => (
    <div>
      <Lbl>{label}</Lbl>
      <div className="mt-1 flex items-center gap-2 text-sm text-[#0F1F3D] font-medium">
        <Icon className="h-4 w-4 text-[#94A3B8] shrink-0" />
        <span className="break-words">{value || "—"}</span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="tender-modal-overlay" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div role="dialog" aria-modal="true" data-testid="tender-modal" onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[680px] max-h-[90vh] overflow-y-auto">

        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-start gap-3 z-10">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                closed ? "bg-[#F1F5F9] text-[#94A3B8]" : dleft !== null && dleft <= 7 ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#ECFDF5] text-[#047857]"
              }`}>
                <CalendarClock className="h-3 w-3" /> {deadline}
              </span>
              {projectName && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#ECFDF5] text-[#047857] text-[11px] font-semibold">{projectName}</span>}
              {typeof t.relevance_score === "number" && <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${relevanceClass(t.relevance_score)}`}>Relevance {t.relevance_score}</span>}
            </div>
            <h2 className="mt-2 text-[19px] leading-[26px] font-bold text-[#0F1F3D]" data-testid="tender-modal-title">{t.title}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="tender-modal-close" className="p-2 rounded-md hover:bg-[#F1F5F9] text-[#64748B]"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field icon={Hash} label="Tender ID" value={t.tender_id} />
            <Field icon={Building2} label="Buyer" value={t.buyer} />
            <Field icon={IndianRupee} label="Value (INR)" value={t.value} />
            <Field icon={CalendarClock} label="Closing date & time (IST)" value={fmtClosingDate} />
            <Field icon={Globe} label="Source" value={t.source} />
            <Field icon={CalendarClock} label="Opening date & time (IST)" value={fmtOpeningDate} />
          </div>

          {t.summary && <div><Lbl>Summary</Lbl><p className="mt-2 text-[15px] leading-[1.7] text-[#0F1F3D]" data-testid="tender-modal-summary">{t.summary}</p></div>}

          {t.recommended_action && <div className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3" data-testid="tender-modal-action">
            <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[#B45309]" /><Lbl>Strategic Recommendations</Lbl></div>
            <p className="mt-2 text-sm text-[#0F1F3D]">{t.recommended_action}</p>
          </div>}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-between gap-3">
          <button onClick={() => onToggleBookmark(t)} data-testid="tender-modal-bookmark"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-semibold ${
              isBookmarked ? "bg-[#0F1F3D] text-white border-[#0F1F3D]" : "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
            }`}>
            {isBookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
            {isBookmarked ? "Saved" : "Save"}
          </button>
          {t.url && <a href={t.url} target="_blank" rel="noreferrer" data-testid="tender-modal-open-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]">
            Open tender <ExternalLink className="h-4 w-4" />
          </a>}
        </div>
      </div>
    </div>
  );
}
